'use client';

import React from 'react';

import { useForm as useFormspree, ValidationError } from '@formspree/react';
import { useForm } from 'react-hook-form';

import { FORMSPREE_FUND_A_PROGRAM } from '~/lib/constants';
import { formatCurrency } from '~/utils/number';

import { Button } from '../ui/button';
import { Icon } from '../ui/icon';
import { Input } from '../ui/input';
import { ProgramSelect } from './hospital-sponsorship-form/program-select';
import { USStatesSprite } from './hospital-sponsorship-form/united-states';

type FormValues = {
  gaming_units: number;
  gaming_total: string;
  gaming_handheld_units: number;
  gaming_handheld_total: string;
  toys_units: number;
  toys_total: string;
  total: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  zip_code: string;
};

const gameCost = 5000;
const gameHandheldCost = 1600;
const toysCost = 400;

export function FundProgramForm() {
  const programUnitSelectSectionEl = React.useRef<HTMLElement>(null);
  const [totalDollars, setTotalDollars] = React.useState(0);

  const { setValue, register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: {
      gaming_units: 0,
      gaming_total: formatCurrency(0),
      gaming_handheld_units: 0,
      gaming_handheld_total: formatCurrency(0),
      toys_units: 0,
      toys_total: formatCurrency(0),
      total: formatCurrency(0),
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      zip_code: '',
    },
  });

  const [formspreeState, sendToFormspree] = useFormspree(FORMSPREE_FUND_A_PROGRAM);

  const handleQuantyUpdate = React.useCallback(
    (op: string, amount: number) => {
      return op === 'add' ? setTotalDollars(totalDollars + amount) : setTotalDollars(totalDollars - amount);
    },
    [totalDollars],
  );

  if (formspreeState.succeeded) {
    return (
      <div className='mx-auto mb-8 flex max-w-xl flex-col items-center gap-3 lg:mb-16'>
        <Icon className='text-yellow' height={40} use='flare' width={80} />
        <h2 className='display-sm text-foreground text-center'>Thank you for your pledge!</h2>
        <div className='text-muted-foreground text-center text-base lg:text-lg'>
          A Starlight staff member will be reaching out to you shortly with next steps.
        </div>
      </div>
    );
  }

  return (
    <section className='mx-auto max-w-3xl px-6 sm:px-12 md:max-w-6xl'>
      <section className='mb-16' ref={programUnitSelectSectionEl}>
        <div className='mx-auto mb-8 flex max-w-xl flex-col items-center gap-3 lg:mb-16'>
          <Icon className='text-yellow' height={40} use='flare' width={80} />
          <h2 className='display-sm text-foreground text-center'>Which program would you like to fund?</h2>
        </div>
        <div className='grid gap-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
          <ProgramSelect
            cost={gameCost}
            image='/img/hospital-sponsorship/gaming.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number) => {
              setValue('gaming_units', unitTotal);
              setValue('gaming_total', formatCurrency(unitTotal * gameCost));
            }}
            step={1}
            title='Starlight Nintendo Switch'
            unitDescription='to fund'
          />
          <ProgramSelect
            cost={gameHandheldCost}
            image='/img/hospital-sponsorship/gaming-handheld.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number) => {
              setValue('gaming_handheld_units', unitTotal);
              setValue('gaming_handheld_total', formatCurrency(unitTotal * gameHandheldCost));
            }}
            step={1}
            title='Starlight Nintendo Switch, Handheld'
            unitDescription='to fund'
          />
          <ProgramSelect
            cost={toysCost}
            image='/img/hospital-sponsorship/toys.jpg'
            onQuantityUpdate={handleQuantyUpdate}
            onUnitAmountChange={(unitTotal: number) => {
              setValue('toys_units', unitTotal);
              setValue('toys_total', formatCurrency(unitTotal * toysCost));
            }}
            step={1}
            title='Starlight Bundle of 75 Toy Sets'
            unitDescription='to fund'
          />
        </div>
      </section>
      <section className='mx-auto flex max-w-2xl flex-col items-center px-6'>
        <div className='bg-muted h-2 w-12' />
        <h3 className='display-base text-foreground mx-0 mt-3 text-center md:mt-8 md:text-5xl lg:mt-10'>
          <small className='block text-xs md:text-lg lg:mb-2 lg:text-2xl'>Your funding pledge:</small>
        </h3>
        <div className='display-base text-foreground mx-0 mb-8 text-center md:mb-8 md:text-5xl lg:mb-10'>
          {formatCurrency(totalDollars)}
        </div>
        <p className='mb-12 text-base md:text-xl'>
          Please complete the form below and a Starlight staff member will contact you to get started with the process
          of funding a Starlight program. Want your program to make an impact in your community? Provide your zip code
          and we will work with you to identify a local hospital in need of Starlight programs.
        </p>
        <form className='grid gap-8' onSubmit={handleSubmit(sendToFormspree)}>
          <div className='grid grid-cols-2 gap-8'>
            <Input
              aria-label='First Name'
              placeholder='First name'
              required
              type='text'
              {...register('first_name', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='first_name' prefix='First Name' />
            <Input
              aria-label='Last Name'
              placeholder='Last name'
              required
              type='text'
              {...register('last_name', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='last_name' prefix='Last Name' />
            <div className='col-span-2'>
              <Input
                aria-label='Email'
                placeholder='Email address'
                required
                type='text'
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    message: 'Invalid email address',
                  },
                })}
              />
              <ValidationError errors={formspreeState.errors} field='email' prefix='Email address' />
            </div>
            <Input
              aria-label='Phone'
              placeholder='Phone number'
              required
              type='text'
              {...register('phone', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='phone' prefix='Phone Number' />
            <Input
              aria-label='Zip code'
              placeholder='Zip code'
              required
              type='text'
              {...register('zip_code', { required: true })}
            />
            <ValidationError errors={formspreeState.errors} field='zip_code' prefix='Zip Code' />
          </div>
          <Button
            className='bg-primary text-primary-foreground'
            disabled={totalDollars === 0 || formspreeState.submitting}
            onClick={() => setValue('total', formatCurrency(totalDollars))}
            type='submit'
          >
            Pledge {formatCurrency(totalDollars)}
          </Button>
        </form>
      </section>
      <USStatesSprite />
    </section>
  );
}
